import { memo } from 'react';
import {
  SingleToolSimilar,
  SOCIAL_MEDIA_BIO_TOOL,
  FONT_GENERATOR_TOOL,
  ToolCategory,
  ImageWithTextListContained,
  INSTAGRAM_CAPTION_TOOL,
  AI_WRITER_TOOL,
  EMOJIS_TOOL,
  SYMBOLS_TOOL,
  WORD_COUNT_TOOL,
  PHOTO_CAPTION,
} from '@quicktools/shared-components';
import { ImageWithTextList } from '@quicktools/shared-components/components/landingPages/ImageWithTextList';
import { ImageWithText } from '@quicktools/shared-components/components/landingPages/ImageWithText';
import TabSection from '@quicktools/shared-components/components/landingPages/TabSection2';
import { Trans, useTranslation } from 'react-i18next';
import { ReactNode } from 'react';
import { IMG_WITH_TEXT_SIZES } from '@quicktools/shared-components/components/landingPages/constants';
import { TFunction } from 'i18next';
import TagLines from '@quicktools/shared-components/components/landingPages/TagLines';
import IconTimer from '@picsart/icons/IconTimer';
import IconTickCircleOutline from '@picsart/icons/IconTickCircleOutline';
import IconLearn from '@picsart/icons/IconLearn';
import IconFaceMask from '@picsart/icons/IconFaceMask';
import ImageWithTextScrollable from '@quicktools/shared-components/components/landingPages/ImageWithTextScrollable';

import analytics from '../../../services/analytics';

import useStyles from './styles';

const TOOLS = [AI_WRITER_TOOL, INSTAGRAM_CAPTION_TOOL, SOCIAL_MEDIA_BIO_TOOL];
const OTHER_TOOLS = [EMOJIS_TOOL, SYMBOLS_TOOL, WORD_COUNT_TOOL, PHOTO_CAPTION];
const TAG_ICONS = [IconTimer, IconTickCircleOutline, IconLearn, IconFaceMask];

const getSteps = (noOfSteps: number, key: string, t: TFunction) => {
  return Array.from(Array(noOfSteps)).map((_, index) => ({
    title: (
      <Trans i18nKey={`${key}.${index}.title`} components={{ bold: <strong />, a: <a /> }} />
    ) as ReactNode,
    description: (
      <Trans i18nKey={`${key}.${index}.description`} components={{ bold: <strong />, a: <a /> }} />
    ) as ReactNode,
    imgAlt: t(`${key}.${index}.imgAlt`),
  }));
};

const generateData = (texts: ReturnType<typeof getSteps>, img: string[]) => {
  return texts.map((obj: { title: ReactNode; description: ReactNode; imgAlt: string }, i) => ({
    ...obj,
    imgSrc: img[i],
    imageRight: i % 2 === 1,
    mobileImageFirst: true,
  }));
};

const IMAGES_WITH_TEXT = [
  {
    src: 'https://pastatic.picsart.com/cms-pastatic/75f05ad7-6489-4d18-881b-ba722c743164.png',
    srcSet:
      'https://pastatic.picsart.com/cms-pastatic/d1cb4b5a-c14f-43d1-bc38-52d50aba7259.png 327w, https://pastatic.picsart.com/cms-pastatic/75f05ad7-6489-4d18-881b-ba722c743164.png 648w',
  },
  {
    src: 'https://pastatic.picsart.com/cms-pastatic/cac65602-a06a-4b18-9720-ccd4c4bef55f.png',
    srcSet:
      'https://pastatic.picsart.com/cms-pastatic/c5d03b8f-ca5c-46c1-91a6-f68ccfb086e8.png 327w, https://pastatic.picsart.com/cms-pastatic/cac65602-a06a-4b18-9720-ccd4c4bef55f.png 648w',
  },
];

const STEPS_IMAGES = [
  {
    src: 'https://pastatic.picsart.com/cms-pastatic/b134d40a-431a-4df6-a7bf-445089ca7815.png',
    mobileSrc: 'https://pastatic.picsart.com/cms-pastatic/a6925e94-1d97-4889-b5c8-ade0f37757fb.png',
  },
  {
    src: 'https://pastatic.picsart.com/cms-pastatic/e58c694f-6371-41d8-87c0-1fa7d7d50d6d.png',
    mobileSrc: 'https://pastatic.picsart.com/cms-pastatic/6a865fa7-69b5-4cf1-a5d3-d28deb4b73f6.png',
  },
  {
    src: 'https://pastatic.picsart.com/cms-pastatic/cc904ab6-ac60-4b88-970a-450093661db3.png',
    mobileSrc: 'https://pastatic.picsart.com/cms-pastatic/ee2d228f-bb61-41da-a3c8-b4e79f114d96.png',
  },
  {
    src: 'https://pastatic.picsart.com/cms-pastatic/53c9b8a8-514b-4b17-9ca7-58ea8b2a7ef5.png',
    mobileSrc: 'https://pastatic.picsart.com/cms-pastatic/2438b79f-a802-4363-bef9-9a4c6c9564a5.png',
  },
  {
    src: 'https://pastatic.picsart.com/cms-pastatic/a69d7039-6c1b-4d3c-9b10-29243222088c.png',
    mobileSrc: 'https://pastatic.picsart.com/cms-pastatic/06bce868-ce63-4191-a5b5-ca3b096d4362.png',
  },
  {
    src: 'https://pastatic.picsart.com/cms-pastatic/a2d41983-e9c0-4525-be90-c6ee8c3af04c.png',
    mobileSrc: 'https://pastatic.picsart.com/cms-pastatic/ca48b3c7-0a79-4936-84fd-2054d6583bd3.png',
  },
  {
    src: 'https://pastatic.picsart.com/cms-pastatic/e1073b6c-20c9-44f1-adf1-52f3cdb0d81f.png',
    mobileSrc: 'https://pastatic.picsart.com/cms-pastatic/19a8e8dd-c2e8-4407-811a-4766dcceaa99.png',
  },
  {
    src: 'https://pastatic.picsart.com/cms-pastatic/aed0c851-ee89-4b8c-b4c2-8b2a9bc26eea.png',
    mobileSrc: 'https://pastatic.picsart.com/cms-pastatic/1e0ce522-b00e-4bf4-8628-b5879e4a53b5.png',
  },
];

function Static() {
  const styles = useStyles();
  const { t } = useTranslation();

  const imagesWithText = [0, 1].map((i) => ({
    title: t(`toolDetailsAll.${i}.title`),
    description: (
      <Trans
        i18nKey={`toolDetailsAll.${i}.description`}
        components={{ a: <a />, br: <br />, strong: <strong /> }}
      />
    ),
    ...IMAGES_WITH_TEXT[i],
    sizes: IMG_WITH_TEXT_SIZES,
  }));

  const tabItems = [0, 1, 2, 3, 4, 5, 6, 7].map((i) => ({
    title: (
      <Trans i18nKey={`stepsAll.items.${i}.title`} components={{ bold: <strong />, a: <a /> }} />
    ),
    description: (
      <Trans
        i18nKey={`stepsAll.items.${i}.description`}
        components={{ bold: <strong />, a: <a /> }}
      />
    ),
    alt: t(`stepsAll.item.${i}.alt`),
    ...STEPS_IMAGES[i],
  }));

  const tagItems = [0, 1, 2, 3].map((i) => ({
    text: <Trans i18nKey={`tagsAll.${i}`} components={{ span: <span />, br: <br /> }} />,
    Icon: TAG_ICONS[i],
  }));

  const scrollableItems = [0, 1, 2].map((i) => ({
    title: (
      <Trans
        i18nKey={`simplifiedStepSectionAll1.steps.${i}.title`}
        components={{ strong: <strong /> }}
      />
    ),
    description: (
      <Trans
        i18nKey={`simplifiedStepSectionAll1.steps.${i}.description`}
        components={{
          strong: <strong />,
          ol: <ol />,
          li: <li />,
          br: <br />,
          ul: <ul />,
          a: <a />,
        }}
      />
    ),
    src: t(`simplifiedStepSectionAll1.steps.${i}.src`),
    alt: t(`simplifiedStepSectionAll1.steps.${i}.alt`),
  }));
  const listContainedItems2 = [0, 1, 2].map((i) => ({
    title: (
      <Trans
        i18nKey={`simplifiedStepSectionAll2.steps.${i}.title`}
        components={{ strong: <strong />, a: <a /> }}
      />
    ),
    src: t(`simplifiedStepSectionAll2.steps.${i}.src`),
    alt: t(`simplifiedStepSectionAll2.steps.${i}.alt`),
    url: t(`simplifiedStepSectionAll2.steps.${i}.url`),
  }));

  return (
    <>
      <div className={styles.root}>
        <TagLines className={styles.tagLines} items={tagItems} />
        <ImageWithTextList items={imagesWithText} />
        <ImageWithTextScrollable
          className={styles.imageWithTextScrollable}
          title={
            <Trans
              i18nKey={`simplifiedStepSectionAll1.title`}
              components={{ strong: <strong /> }}
            />
          }
          items={scrollableItems}
        />
        <TabSection title={t('stepsAll.title')} items={tabItems} />
        <ImageWithText
          alt={t('toolDetails2All.0.title')}
          title={t('toolDetails2All.0.title')}
          description={
            <Trans
              i18nKey={`toolDetails2All.0.description`}
              components={{ a: <a />, br: <br />, strong: <strong /> }}
            />
          }
          src="https://pastatic.picsart.com/cms-pastatic/a2d83c72-c8fe-4d3b-bb0c-3c1a4a9e8ab4.png"
          srcSet="https://pastatic.picsart.com/cms-pastatic/35f5d092-de60-479f-909e-aff48ee027e0.png 327w, https://pastatic.picsart.com/cms-pastatic/a2d83c72-c8fe-4d3b-bb0c-3c1a4a9e8ab4.png 648w"
          sizes={IMG_WITH_TEXT_SIZES}
        />
        <ImageWithTextListContained
          className={styles.imageWithTextListContained}
          items={listContainedItems2}
          title={
            <Trans
              i18nKey={`simplifiedStepSectionAll2.title`}
              components={{ strong: <strong /> }}
            />
          }
        />

        <SingleToolSimilar
          similarHeading={t('similarToolsTitle')}
          current={FONT_GENERATOR_TOOL}
          analytics={analytics}
          className={styles.similar}
          similar={TOOLS}
          otherTools={OTHER_TOOLS}
          category={ToolCategory.Text}
        />
      </div>
    </>
  );
}

export default memo(Static);
